import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, PlayCircle, Clock, CheckCircle, XCircle, Calendar, ArrowLeft, BarChart3, Camera, Search, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function AutomationDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);

  const { data: lastExecution, refetch: refetchExecution } = useQuery({
    queryKey: ['last-cron-execution'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cron_executions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    refetchInterval: 30000
  });

  const { data: cronStatus } = useQuery({
    queryKey: ['cron-status'],
    queryFn: async () => {
      try {
        const { data } = await supabase.functions.invoke('check-cron-status');
        return data;
      } catch {
        return { isSetup: false };
      }
    }
  });

  const runNow = useMutation({
    mutationFn: async () => {
      setIsRunning(true);
      toast.info('Starting automation pipeline...', {
        description: 'This will take 3-5 minutes'
      });

      const { data, error } = await supabase.functions.invoke('daily-automation', {
        body: { force: true }
      });

      if (error) {
        throw {
          message: error.message,
          details: error.details || error.context || 'No additional details',
          data: data
        };
      }

      // Check if data contains error info
      if (data?.skipped) {
        return data;
      }

      if (data?.steps) {
        const failedSteps = data.steps.filter((s: any) => s.status === 'failed');
        if (failedSteps.length > 0) {
          throw {
            message: 'Automation completed with errors',
            failedSteps,
            data
          };
        }
      }

      return data;
    },
    onSuccess: (data) => {
      if (data?.skipped) {
        setLastResult({ 
          success: false, 
          error: data.reason,
          details: `Last run: ${new Date(data.lastRun).toLocaleString()}`,
          timestamp: new Date() 
        });
        toast.warning('Automation skipped', {
          description: data.reason
        });
      } else {
        setLastResult({ success: true, data, timestamp: new Date() });
        toast.success('Automation completed successfully!', {
          description: `Published ${data?.articlesPublished || 0} articles`
        });
        queryClient.invalidateQueries({ queryKey: ['last-cron-execution'] });
        queryClient.invalidateQueries({ queryKey: ['cron-executions-history'] });
      }
    },
    onError: (error: any) => {
      const errorDetails = {
        message: error.message || 'Unknown error',
        failedSteps: error.failedSteps || [],
        details: error.details,
        data: error.data,
        timestamp: new Date()
      };
      
      setLastResult({ success: false, ...errorDetails });
      
      toast.error('Automation failed', {
        description: error.message || 'Check the error details below'
      });
    },
    onSettled: () => {
      setIsRunning(false);
    }
  });

  const getNextRunTime = () => {
    const now = new Date();
    const nextRun = new Date();
    nextRun.setHours(18, 0, 0, 0);
    
    if (now.getHours() >= 18) {
      nextRun.setDate(nextRun.getDate() + 1);
    }
    
    const hoursUntil = Math.floor((nextRun.getTime() - now.getTime()) / (1000 * 60 * 60));
    const minutesUntil = Math.floor(((nextRun.getTime() - now.getTime()) % (1000 * 60 * 60)) / (1000 * 60));
    
    return { nextRun, hoursUntil, minutesUntil };
  };

  const { nextRun, hoursUntil, minutesUntil } = getNextRunTime();

  return (
    <div className="container mx-auto p-8 max-w-6xl">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-4xl font-bold">Automation Control</h1>
          </div>
          <p className="text-muted-foreground">Manage your daily crypto content automation system</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/analytics')}>
          <BarChart3 className="w-4 h-4 mr-2" />
          Analytics
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Daily Cron Job</div>
              <div className={`flex items-center gap-2 text-lg font-semibold ${
                cronStatus?.isSetup ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
              }`}>
                {cronStatus?.isSetup ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Active
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    Not Activated
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="text-sm text-muted-foreground mb-1">Last Execution</div>
              <div className="text-lg font-semibold">
                {lastExecution ? (
                  <div>
                    <div className={lastExecution.status === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {lastExecution.status === 'success' ? (
                        <CheckCircle className="w-5 h-5 inline mr-2" />
                      ) : (
                        <XCircle className="w-5 h-5 inline mr-2" />
                      )}
                      {lastExecution.status.toUpperCase()}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {new Date(lastExecution.created_at).toLocaleString('en-AU')}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">Never run</div>
                )}
              </div>
            </div>

            <div>
              <div className="text-sm text-muted-foreground mb-1">Next Scheduled Run</div>
              <div className="text-lg font-semibold">
                {cronStatus?.isSetup ? (
                  <div>
                    <div className="text-primary">
                      <Calendar className="w-5 h-5 inline mr-2" />
                      {hoursUntil}h {minutesUntil}m
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {nextRun.toLocaleString('en-AU', { 
                        weekday: 'short',
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">Not scheduled</div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {!cronStatus?.isSetup && (
        <Alert className="mb-6 border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20">
          <AlertDescription className="flex items-start gap-2">
            <XCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-yellow-800 dark:text-yellow-400 mb-1">Daily Automation Not Active</div>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                Your automation system is built and ready, but the daily scheduler hasn't been activated yet. 
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-primary underline ml-1"
                  onClick={() => navigate('/admin/cron-setup')}
                >
                  Go to Cron Setup
                </Button> to activate it.
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="w-5 h-5" />
            Manual Control
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Manually trigger the full automation pipeline to generate articles immediately. 
              This will collect market data, analyze opportunities, and generate 2 new articles.
            </p>
            
            <Button 
              onClick={() => runNow.mutate()}
              disabled={isRunning}
              size="lg"
              className="w-full md:w-auto"
            >
              {isRunning ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Running Automation... (3-5 minutes)
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-5 w-5" />
                  Run Automation Now
                </>
              )}
            </Button>

            {lastResult && (
              <Alert className={
                lastResult.success 
                  ? 'border-green-600 bg-green-50 dark:bg-green-900/20' 
                  : 'border-red-600 bg-red-50 dark:bg-red-900/20'
              }>
                <AlertDescription>
                  <div className="font-semibold mb-2">
                    {lastResult.success ? '✅ Success!' : '❌ Failed'}
                  </div>
                  {lastResult.success ? (
                    <div className="text-sm space-y-1">
                      <div>Articles Published: {lastResult.data?.articlesPublished || 0}</div>
                      <div>Execution Time: {lastResult.data?.executionTime || 'N/A'}s</div>
                      <div className="mt-2">
                        <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/')}>
                          View new articles on homepage →
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm space-y-3">
                      <div className="font-semibold text-red-700 dark:text-red-300">
                        {lastResult.message || lastResult.error}
                      </div>
                      
                      {lastResult.details && (
                        <div className="text-xs opacity-80">
                          {lastResult.details}
                        </div>
                      )}

                      {lastResult.failedSteps && lastResult.failedSteps.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="font-semibold">Failed Steps:</div>
                          {lastResult.failedSteps.map((step: any, idx: number) => (
                            <div key={idx} className="pl-3 border-l-2 border-red-400 dark:border-red-600">
                              <div className="font-medium">Layer {step.layer}: {step.name}</div>
                              <div className="text-xs opacity-80 mt-1">{step.error}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {lastResult.data && (
                        <details className="mt-3">
                          <summary className="cursor-pointer text-xs font-medium opacity-70 hover:opacity-100">
                            View Full Response
                          </summary>
                          <pre className="mt-2 p-2 bg-black/10 dark:bg-white/10 rounded text-xs overflow-auto max-h-48">
                            {JSON.stringify(lastResult.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual Trigger Buttons */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <QuickActionButton
              label="Force Price Snapshot"
              description="Snapshot all token prices to daily_snapshots"
              icon={<Camera className="w-4 h-4" />}
              functionName="snapshot-daily-prices"
            />
            <QuickActionButton
              label="Run Strategic Screen"
              description="EMA/MACD/RSI/Volume screener on all tokens"
              icon={<Search className="w-4 h-4" />}
              functionName="run-strategic-screen"
            />
            <QuickActionButton
              label="Force Market Refresh"
              description="Refresh market overview data"
              icon={<TrendingUp className="w-4 h-4" />}
              functionName="fetch-market-overview"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Executions</CardTitle>
        </CardHeader>
        <CardContent>
          <ExecutionHistory />
        </CardContent>
      </Card>
    </div>
  );
}

function QuickActionButton({ label, description, icon, functionName }: { 
  label: string; description: string; icon: React.ReactNode; functionName: string 
}) {
  const [isRunning, setIsRunning] = useState(false);

  const handleRun = async () => {
    setIsRunning(true);
    toast.info(`Starting ${label}...`);
    try {
      const { data, error } = await supabase.functions.invoke(functionName);
      if (error) throw error;
      toast.success(`${label} completed`, {
        description: data?.tokensSnapshotted 
          ? `${data.tokensSnapshotted} tokens processed`
          : data?.passedAllCriteria !== undefined
            ? `${data.passedAllCriteria} tokens passed all criteria`
            : 'Done'
      });
    } catch (err: any) {
      toast.error(`${label} failed`, { description: err.message });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="p-4 rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 mb-2 font-semibold text-sm">{icon} {label}</div>
      <p className="text-xs text-muted-foreground mb-3">{description}</p>
      <Button size="sm" variant="outline" onClick={handleRun} disabled={isRunning} className="w-full">
        {isRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {isRunning ? 'Running...' : 'Run Now'}
      </Button>
    </div>
  );
}

function ExecutionHistory() {
  const { data: executions } = useQuery({
    queryKey: ['cron-executions-history'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cron_executions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      return data || [];
    },
    refetchInterval: 30000
  });

  if (!executions || executions.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No executions yet. Run the automation manually to see results.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {executions.map((execution) => (
        <div 
          key={execution.id}
          className={`p-4 rounded-lg border ${
            execution.status === 'success'
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-700'
          }`}
        >
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {execution.status === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                )}
                <span className="font-semibold">
                  {execution.status === 'success' ? 'Success' : 'Failed'}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                {new Date(execution.created_at).toLocaleString('en-AU')}
              </div>
              {execution.articles_published !== null && execution.articles_published !== undefined && (
                <div className="text-sm mt-1">
                  Articles: {execution.articles_published}
                </div>
              )}
            </div>
            <div className="text-right text-sm text-muted-foreground">
              {execution.execution_time_seconds 
                ? `${execution.execution_time_seconds.toFixed(1)}s`
                : 'N/A'
              }
            </div>
          </div>
          {execution.error_message && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-300">
              Error: {execution.error_message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
